import React, { useState } from 'react';
import { Form } from 'rsuite';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGoogle } from '@fortawesome/free-brands-svg-icons';
import CheckboxFormField from 'components/Form/Input/Base/CheckboxFormField';
import TextFormField from 'components/Form/Input/Base/TextFormField';
import useSignIn from 'features/auth/hooks/useSignIn';
import ParagraphText from 'components/Elements/ParagraphText/ParagraphText';
import Button from 'components/Elements/Button/Button';
import { NavLink } from 'react-router-dom';
import styles from './LoginForm.module.scss';

const checkboxes = [
  {
    label: 'Remember me',
    value: 'true',
  },
];

type LoginFormProps = {
  onEmailModeChange?: (isEmailMode: boolean) => void;
};

function LoginForm({ onEmailModeChange = undefined }: LoginFormProps) {
  const [showEmailForm, setShowEmailForm] = useState(false);

  const {
    model,
    formValue,
    isLoading,
    submit,
    signInWithGoogle,
    setFormValue,
    pendingGoogleEmail,
    completeLinking,
    cancelLinking,
  } = useSignIn();

  return (
    <div className={styles.container}>
      <Form
        fluid
        model={model}
        formValue={formValue}
        onChange={setFormValue}
      >
        {pendingGoogleEmail && (
          <div className={styles.formSection}>
            <div className={styles.emailModeHeader}>
              <button
                className={styles.cancelButton}
                type="button"
                aria-label="Cancel linking"
                onClick={cancelLinking}
              >
                ×
              </button>
            </div>

            <ParagraphText marginBottom={16}>
              {`An account with ${pendingGoogleEmail} already exists. Enter your password to link your Google account.`}
            </ParagraphText>

            <TextFormField
              required
              size="lg"
              type="password"
              name="password"
              label="Password"
            />

            <Button
              size="full"
              type="button"
              isLoading={isLoading}
              onClick={() => completeLinking(formValue.password)}
            >
              <ParagraphText textAlign="center" margin={0} color="#fff">
                Link &amp; Sign In
              </ParagraphText>
            </Button>
          </div>
        )}

        {!pendingGoogleEmail && !showEmailForm && (
          <div className={styles.authChoiceContainer}>
            <Button
              className={styles.darkGoogleButton}
              size="full"
              type="button"
              onClick={signInWithGoogle}
              startIcon={<FontAwesomeIcon color="#fff" icon={faGoogle} />}
            >
              <ParagraphText textAlign="center" margin={0} color="#fff">
                Sign in with Google
              </ParagraphText>
            </Button>

            <Button
              size="full"
              type="button"
              color="blue"
              onClick={() => {
                setShowEmailForm(true);
                onEmailModeChange?.(true);
              }}
            >
              <ParagraphText textAlign="center" margin={0} color="#fff">
                Continue with Email
              </ParagraphText>
            </Button>

            <div className={styles.linksContainer}>
              <NavLink className={styles.customLink} to="/sign-up">
                <p>New to My TXT? Create an account</p>
              </NavLink>
            </div>
          </div>
        )}

        {!pendingGoogleEmail && showEmailForm && (
          <div className={styles.formSection}>
            <div className={styles.emailModeHeader}>
              <button
                className={styles.cancelButton}
                type="button"
                aria-label="Back to sign in options"
                onClick={() => {
                  setShowEmailForm(false);
                  onEmailModeChange?.(false);
                }}
              >
                ×
              </button>
            </div>

            <TextFormField
              required
              size="lg"
              type="email"
              name="email"
              label="Email"
            />

            <TextFormField
              required
              size="lg"
              type="password"
              name="password"
              label="Password"
            />

            <CheckboxFormField
              name="saveEmail"
              label=""
              size="lg"
              checkboxes={checkboxes}
            />

            <Button
              size="full"
              type="submit"
              isLoading={isLoading}
              onClick={submit}
            >
              <ParagraphText textAlign="center" margin={0} color="#fff">
                Sign In
              </ParagraphText>
            </Button>

            <div className={styles.linksContainer}>
              <NavLink className={styles.customLink} to="/forgot-password">
                <p>Forgot your password?</p>
              </NavLink>
              <NavLink className={styles.customLink} to="/sign-up">
                <p>New to My TXT? Create an account</p>
              </NavLink>
            </div>
          </div>
        )}
      </Form>
    </div>
  );
}

export default LoginForm;
